/**
 * Six boxes for a 6-digit code. Typing moves forward, Backspace moves back,
 * arrows move, pasting "482 197" fills them all, and the sixth digit sends
 * the code by itself. The first box carries `one-time-code`, so phones
 * offer the code from the email.
 */
import { useEffect, useRef, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react'

const LENGTH = 6

export function CodeBoxes({
  value, onChange, onComplete, disabled = false, invalid = false, autoFocus = true,
  label = 'Code à 6 chiffres', digitLabel = (n) => `Chiffre ${n} sur ${LENGTH}`,
}: {
  value: string
  onChange: (value: string) => void
  onComplete: (code: string) => void
  disabled?: boolean
  invalid?: boolean
  autoFocus?: boolean
  /** Screen-reader labels; French by default (the console), translated by the dashboard. */
  label?: string
  digitLabel?: (n: number) => string
}) {
  const boxes = useRef<(HTMLInputElement | null)[]>([])
  const digits = value.replace(/\D/g, '').slice(0, LENGTH).split('')

  useEffect(() => {
    if (autoFocus && !disabled) boxes.current[Math.min(digits.length, LENGTH - 1)]?.focus()
    // Focus follows the code only when it is cleared or the boxes open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus, disabled, value === ''])

  const set = (next: string[], focusAt: number) => {
    const code = next.join('').slice(0, LENGTH)
    onChange(code)
    boxes.current[Math.max(0, Math.min(focusAt, LENGTH - 1))]?.focus()
    if (code.length === LENGTH) onComplete(code)
  }

  const fillFrom = (index: number, text: string) => {
    const incoming = text.replace(/\D/g, '')
    if (!incoming) return
    const next = digits.slice(0, index)
    for (const d of incoming) {
      if (next.length >= LENGTH) break
      next.push(d)
    }
    set(next, next.length)
  }

  const input = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const typed = e.target.value.replace(/\D/g, '')
    // Boxes fill in order: a digit typed further right lands in the next free box.
    const at = Math.min(index, digits.length)
    if (!typed) {
      if (digits[index]) set([...digits.slice(0, index), ...digits.slice(index + 1)], index)
      return
    }
    // Several digits at once: the phone filled the code in, or a paste without the event.
    if (typed.length > 2 || (typed.length === 2 && !digits[index])) {
      fillFrom(at, typed)
      return
    }
    const next = [...digits]
    next[at] = typed[typed.length - 1]
    set(next, at + 1)
  }

  const keyDown = (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      if (digits[index]) {
        set([...digits.slice(0, index), ...digits.slice(index + 1)], index)
      } else if (index > 0) {
        set([...digits.slice(0, index - 1), ...digits.slice(index)], index - 1)
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      boxes.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < LENGTH - 1) {
      e.preventDefault()
      boxes.current[index + 1]?.focus()
    }
  }

  const paste = (index: number) => (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    fillFrom(index, e.clipboardData.getData('text'))
  }

  return (
    <div role="group" aria-label={label} className="grid grid-cols-6 gap-2 sm:gap-3">
      {Array.from({ length: LENGTH }, (_, i) => (
        <input
          key={i}
          ref={(el) => { boxes.current[i] = el }}
          value={digits[i] ?? ''}
          onChange={input(i)}
          onKeyDown={keyDown(i)}
          onPaste={paste(i)}
          onFocus={(e) => e.target.select()}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={digitLabel(i + 1)}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          maxLength={LENGTH}
          className={`h-14 w-full min-w-0 rounded-xl border bg-white text-center font-mono text-2xl font-semibold text-slate-900 outline-none transition-colors
            focus:ring-4 disabled:opacity-60 sm:h-16 dark:bg-slate-900 dark:text-slate-100
            ${invalid
              ? 'border-red-400 focus:border-red-500 focus:ring-red-500/15 dark:border-red-500 dark:focus:border-red-400'
              : 'border-slate-300 focus:border-[#0A192F] focus:ring-[#0A192F]/10 dark:border-slate-700 dark:focus:border-slate-300 dark:focus:ring-white/10'}`}
        />
      ))}
    </div>
  )
}
